import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Certifications = () => {
  const { certifications } = useSelector(state => state.certifications);
  const [expandedSkills, setExpandedSkills] = useState({});

  useEffect(() => {
    document.title = "Certifications | Musa Yusuf Magoban";
    window.scrollTo(0, 0);
  }, []);

  const toggleSkills = (certId) => {
    setExpandedSkills(prev => ({
      ...prev,
      [certId]: !prev[certId]
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Certifications | Musa Yusuf Magoban</title>
        <meta name="description" content="Professional cybersecurity certifications including Microsoft SC-200, ISC2 CC, and specialized security credentials with verification links." />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Header Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                Professional Certifications
              </h1>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Industry-recognized certifications validating expertise in cybersecurity, 
                SOC operations, and information security management.
              </p>
              <div className="mt-6 flex justify-center">
                <div className="inline-flex items-center px-4 py-2 bg-accent/10 rounded-full">
                  <Icon name="Award" size={20} className="text-accent mr-2" />
                  <span className="text-accent font-medium">{certifications.filter(cert => cert.status === 'Active').length} Active Certifications</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Certifications Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {certifications.map((cert) => (
                <div key={cert.id} className="bg-surface rounded-lg p-6 security-shadow hover:security-shadow-strong transition-all duration-300 group">
                  {/* Badge and Status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent/20 to-primary/20 rounded-lg flex items-center justify-center p-2 group-hover:scale-105 transition-transform">
                      {cert.badgeUrl ? (
                        <img 
                          src={cert.badgeUrl} 
                          alt={`${cert.name} badge`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <Icon name="Award" size={24} className="text-accent" style={{display: cert.badgeUrl ? 'none' : 'flex'}} />
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                      {cert.status}
                    </span>
                  </div>
                  
                  {/* Certification Info */}
                  <h3 className="text-lg font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {cert.name}
                  </h3>
                  
                  <p className="text-sm text-text-secondary mb-4 line-clamp-3">
                    {cert.description}
                  </p>
                  
                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="Building" size={16} className="mr-2 text-primary" />
                      {cert.issuer}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="Calendar" size={16} className="mr-2 text-primary" />
                      {cert.date}
                    </div>
                    {cert.credentialId && (
                      <div className="flex items-center text-sm text-text-secondary">
                        <Icon name="Hash" size={16} className="mr-2 text-primary" />
                        <span className="font-mono text-xs">{cert.credentialId}</span>
                      </div>
                    )}
                  </div>

                  {/* Skills */}
                  {cert.skills && cert.skills.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-text-secondary font-medium">Skills ({cert.skills.length})</span>
                        {cert.skills.length > 3 && (
                          <button
                            onClick={() => toggleSkills(cert.id)}
                            className="text-xs text-primary hover:text-primary/80 font-medium flex items-center transition-colors"
                          >
                            {expandedSkills[cert.id] ? (
                              <>
                                <Icon name="ChevronUp" size={12} className="mr-1" />
                                Show Less
                              </>
                            ) : (
                              <>
                                <Icon name="ChevronDown" size={12} className="mr-1" />
                                Show All
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(expandedSkills[cert.id] ? cert.skills : cert.skills.slice(0, 3)).map((skill, skillIndex) => (
                          <span key={skillIndex} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-primary/10 text-primary">
                            {skill}
                          </span>
                        ))}
                        {!expandedSkills[cert.id] && cert.skills.length > 3 && (
                          <button
                            onClick={() => toggleSkills(cert.id)}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-text-secondary/10 text-text-secondary hover:bg-text-secondary/20 transition-colors"
                          >
                            +{cert.skills.length - 3} more
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Verification Button */}
                  {cert.verificationUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ExternalLink"
                      iconPosition="right"
                      fullWidth
                      className="group-hover:border-primary group-hover:text-primary transition-colors"
                      onClick={() => window.open(cert.verificationUrl, '_blank', 'noopener,noreferrer')}
                    >
                      Verify Certificate
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary to-secondary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-white">
                Continuous Learning & Growth
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                Committed to staying current with evolving cybersecurity threats and technologies 
                through ongoing certification and professional development.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="lg"
                  iconName="FolderOpen"
                  iconPosition="left"
                  className="bg-white/10 text-white border border-white/20 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => window.location.href = '/projects'}
                >
                  View Projects
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Mail"
                  iconPosition="left"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => window.location.href = '/contact'}
                >
                  Get In Touch
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Certifications; 